import {
  useAppConfigContext,
  useAppConstantsContext,
} from '@ltvco/refresh-lib/ctx';
import { useFileService } from '@ltvco/refresh-lib/hooks';
import { Grid } from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { UploadAttachmentsModal } from '@ltvco/refresh-lib/v2';
import { useQueryClient } from '@ltvco/refresh-lib/vendors';
import { useCallback, useState } from 'react';

import { constants } from 'appConstants';

import { BulkImportPromoCard } from './propertyReportPromo/BulkImportPromoCard';
import { ExploreLocalCrimePromoCard } from './propertyReportPromo/ExploreLocalCrimePromoCard';
import {
  BusinessPromoSection,
  NewFeatureBadge,
  NewFeatureIcon,
  NewFeaturesHeaderStack,
  NewFeaturesHeading,
} from './propertyReportPromo/PropertyReportBusinessPromoRow.styles';
import { ReportAttachmentsPromoCard } from './propertyReportPromo/ReportAttachmentsPromoCard';

export interface PropertyReportBusinessPromoRowProps {
  userCode: string;
  permalink: string;
  reportType?: string;
  parcelAddress: { parsed?: { zip5?: string | null } | null } | null;
  howAttachmentsCTA?: boolean;
}

export function PropertyReportBusinessPromoRow({
  userCode,
  permalink,
  reportType = 'property',
  parcelAddress,
  howAttachmentsCTA = false,
}: PropertyReportBusinessPromoRowProps) {
  const { isMedium } = useScreenSize();
  const isCompactPromoLayout = isMedium;
  const {
    images,
    data: { brandName },
  } = useAppConstantsContext();
  const { imgBulkImport, imgCloudUpload, imgCrimeWatch } = images as {
    imgBulkImport?: string;
    imgCloudUpload?: string;
    imgCrimeWatch?: string;
  };
  const {
    trackEventGA4,
    routingUtils: { navigate },
  } = useAppConfigContext();
  const queryClient = useQueryClient();
  const brand = brandName.toLowerCase();

  const { data: filesResponse, isLoading: isLoadingFiles } = useFileService({
    userCode,
    permalink,
    reportType,
  });

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const handleRefetch = useCallback(() => {
    queryClient.refetchQueries({ queryKey: ['oram-files', permalink] });
  }, [permalink, queryClient]);

  const handleUploadClick = useCallback(() => {
    trackEventGA4({
      event_name: 'engagement_click',
      type: 'RA_null_click',
    });
    setIsUploadModalOpen(true);
  }, [trackEventGA4]);

  const handleCloseModal = useCallback(() => {
    setUploadFile(null);
    setIsUploadModalOpen(false);
    handleRefetch();
  }, [handleRefetch]);

  const hasNoAttachments = (filesResponse?.attachments?.length ?? 0) === 0;
  const showAttachmentsTile =
    howAttachmentsCTA && hasNoAttachments && !isLoadingFiles;

  const onSeeRecentIncidents = () => {
    const zipcode = parcelAddress?.parsed?.zip5?.trim();
    trackEventGA4({
      event_name: 'report_engagement',
      type: 'crime_cta',
      interacted_from: 'crime_feed_cta_promo_tile',
    });
    const query = zipcode ? `?address=${encodeURIComponent(zipcode)}` : '';
    navigate(`/dashboard/neighborhood-report${query}`);
  };

  const bulkPath = (
    constants.links as { propertyBulkUploadDashboardPath?: string }
  ).propertyBulkUploadDashboardPath;

  const onBulkUpload = () => {
    if (!bulkPath) {
      return;
    }
    trackEventGA4({
      event_name: 'report_engagement',
      type: 'bulk_upload_cta',
      interacted_from: 'property_report_business_row',
    });
    navigate(bulkPath);
  };

  const bulkSlot = Boolean(bulkPath);
  const visibleCardCount =
    (bulkSlot ? 1 : 0) + 1 + (showAttachmentsTile ? 1 : 0);
  const mdColumns = visibleCardCount >= 3 ? 4 : visibleCardCount === 2 ? 6 : 12;

  const bulkIllustration = imgBulkImport ? (
    <img src={imgBulkImport} alt="" aria-hidden />
  ) : null;
  const crimeIllustration = imgCrimeWatch ? (
    <img src={imgCrimeWatch} alt="" aria-hidden />
  ) : null;
  const attachmentsIllustration = imgCloudUpload ? (
    <img src={imgCloudUpload} alt="" aria-hidden />
  ) : null;

  return (
    <>
      <BusinessPromoSection $compact={isCompactPromoLayout}>
        <NewFeaturesHeaderStack
          direction="row"
          alignItems="center"
          columnGap={1}
        >
          {isCompactPromoLayout ? (
            <NewFeatureBadge component="span" aria-hidden>
              NEW
            </NewFeatureBadge>
          ) : (
            <NewFeatureIcon aria-hidden />
          )}
          <NewFeaturesHeading component="h2">New Features</NewFeaturesHeading>
        </NewFeaturesHeaderStack>

        <Grid
          container
          columnSpacing={isCompactPromoLayout ? 0 : 1.25}
          rowSpacing={isCompactPromoLayout ? 2.5 : 1.25}
          sx={isCompactPromoLayout ? undefined : { alignItems: 'stretch' }}
        >
          {bulkPath ? (
            <BulkImportPromoCard
              isCompactLayout={isCompactPromoLayout}
              mdColumns={mdColumns}
              illustration={bulkIllustration}
              onBulkUpload={onBulkUpload}
            />
          ) : null}

          <ExploreLocalCrimePromoCard
            isCompactLayout={isCompactPromoLayout}
            mdColumns={mdColumns}
            illustration={crimeIllustration}
            onCheckIncidents={onSeeRecentIncidents}
          />

          {showAttachmentsTile ? (
            <ReportAttachmentsPromoCard
              isCompactLayout={isCompactPromoLayout}
              mdColumns={mdColumns}
              illustration={attachmentsIllustration}
              onUploadClick={handleUploadClick}
            />
          ) : null}
        </Grid>
      </BusinessPromoSection>

      <UploadAttachmentsModal
        open={isUploadModalOpen}
        onClose={handleCloseModal}
        file={uploadFile}
        userCode={userCode}
        permalink={permalink}
        brand={brand}
        reportType={reportType}
        onFileSelect={setUploadFile}
        onUploadComplete={handleRefetch}
        onFileDeleted={handleCloseModal}
        onResetUploadFile={() => setUploadFile(null)}
        entityType="Report"
      />
    </>
  );
}
